import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    BillingQueryParams,
    type NavigationScreenSiteState,
    resolveNavigationScreenToURL,
} from '@gitbook/app-navigation';
import {
    getSiteProductDefinition,
    SITE_PRICING_PRODUCTS,
    type SiteFeatureDefinition,
    type SitePricingDefinition,
} from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    type ButtonProps,
    ButtonSkeleton,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Heading,
    toast,
} from '@gitbook/spine-react';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import { DocumentationLink } from '../../../../../Components/Link';
import { useNavigationState, useSetAnalyticsScreen } from '../../../../../navigation';
import {
    apiClient,
    apiQuery,
    useAssertCurrentOrganizationId,
    useRedirectToStripe,
    useTrial,
} from '../../../../../state';
import { capitalizeFirstLetter } from '../../../../../utils';
import { GainedFeaturesList } from '../FeaturesLists';
import { type SiteUpgradeModalState, useSiteUpgradeModal } from '../useSiteUpgradeModal';
import { useUpgradeStatusModal } from '../useUpgradeStatusModal';

/**
 * The "Feature" step of the site upgrade modal.
 */
export function SiteUpgradeModalStepFeature(props: {
    featureTrigger?: SiteFeatureDefinition;
    targetSiteType: api.SiteType;
}) {
    const { featureTrigger, targetSiteType } = props;
    const { upgradeState, open: openSiteUpgradeModal, close, site } = useSiteUpgradeModal();
    const organizationId = useAssertCurrentOrganizationId();
    assertIsDefined(site);

    const currentSitePlan = getSiteProductDefinition(site.type);
    assertIsDefined(currentSitePlan);
    const targetSitePlan = SITE_PRICING_PRODUCTS.find((product) => product.key === targetSiteType);
    assertIsDefined(targetSitePlan);
    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', { organizationId })
    );
    const trial = useTrial({ organization });

    assertIsDefined(upgradeState);

    useSetAnalyticsScreen(
        analytics.createSiteEvent(
            { id: site.id, organization: organizationId },
            {
                screen: 'site_plan_modal_plans',
                isNewPricing: true,
                reason: upgradeState.reason,
            }
        ),
        [upgradeState.reason, site.id, organizationId]
    );

    const { modalTitle, featureTriggerParagraph, checkoutInformation } = getUpgradeDialogCopy({
        featureTrigger,
        targetSitePlan,
        site,
        trialStatus: trial.status,
    });

    return (
        <>
            <DialogHeader className="max-w-128">
                <Heading>{modalTitle}</Heading>
            </DialogHeader>

            <DialogBody className="flex max-w-128 flex-col items-start justify-start gap-4 text-muted ">
                {featureTriggerParagraph}

                <div className="flex flex-col gap-2">
                    <p>Enabling {featureTrigger?.title.toLowerCase()} also unlocks:</p>
                    <GainedFeaturesList
                        triggerFeatureKey={featureTrigger?.key}
                        from={site.type}
                        to={targetSiteType}
                    />
                </div>

                {checkoutInformation}
            </DialogBody>

            <DialogFooter>
                <DialogCloseButton label="Maybe later" />
                <React.Suspense
                    fallback={<ButtonSkeleton label={`Upgrade to ${targetSitePlan.title}`} />}
                >
                    <UpgradeButton
                        organizationId={organizationId}
                        site={site}
                        toPlan={targetSiteType}
                        closeModal={close}
                        openPlanModal={openSiteUpgradeModal}
                        reason={upgradeState.reason}
                        trialStatus={trial.status}
                    />
                </React.Suspense>
            </DialogFooter>
        </>
    );
}

function UpgradeButton(props: {
    organizationId: string;
    site: api.Site;
    toPlan: api.SiteType;
    closeModal: () => void;
    openPlanModal: (site: api.Site, modalState: SiteUpgradeModalState) => void;
    reason: api.SiteUpgradeReason;
    trialStatus: api.BillingTrialStatus;
}) {
    const { organizationId, site, toPlan, closeModal, openPlanModal, reason, trialStatus } = props;
    const canStartTrial = trialStatus === api.BillingTrialStatus.None;

    const { data: preview } = useSuspenseQuery(
        apiQuery('previewSitePlanUpdate', { organizationId, siteId: site.id, plan: toPlan })
    );

    const { openUpgradeStatusModal } = useUpgradeStatusModal();
    const redirectToStripe = useRedirectToStripe();
    const { screen: currentScreen } = useNavigationState();
    const targetSitePlan = getSiteProductDefinition(toPlan);
    assertIsDefined(targetSitePlan);

    async function updateSitePlan(plan: api.SiteType) {
        const { data: updateSitePlanResult } = await apiClient.sites.updateSitePlan(
            organizationId,
            site.id,
            {
                plan,
                reason,
            },
            {
                successReturnURL: resolveNavigationScreenToURL({
                    ...(currentScreen as NavigationScreenSiteState),
                    billing: BillingQueryParams.UpgradeSuccess,
                }),
                cancelReturnURL: resolveNavigationScreenToURL({
                    ...(currentScreen as NavigationScreenSiteState),
                }),
            }
        );

        switch (updateSitePlanResult.type) {
            case 'success':
                // Customer subscription was upgraded. Show upgrade status dialog if payment was impacted.
                closeModal();
                updateSitePlanResult.outcome === 'no-payment-changes'
                    ? toast.success(`Your plan was switched to ${targetSitePlan.title}.`)
                    : openUpgradeStatusModal();

                break;

            case 'checkout':
                // Customer needs a new subscription.
                closeModal();
                await redirectToStripe(updateSitePlanResult.sessionId);
                break;

            default:
                throw new Error('Unexpected updateSitePlan outcome for new pricing plan update');
        }
    }

    const buttonProps: Pick<ButtonProps, 'label' | 'onPress' | 'kind' | 'leadingIcon'> = (() => {
        // The button we show when a trial is available.
        if (preview.type === 'trial-available') {
            return {
                kind: 'upgrade',
                leadingIcon: Icon.Upgrade,
                label: 'Start a 14-day free trial',
                onPress: async () => {
                    await toast.catch(async () => {
                        const { data: results } = await apiClient.sites.updateSitePlan(
                            organizationId,
                            site.id,
                            {
                                plan: toPlan,
                                reason,
                            }
                        );

                        switch (results.type) {
                            case 'success':
                                return closeModal();
                        }
                    }, 'Error while upgrading');
                    return;
                },
            };
        }

        const targetSitePlan = SITE_PRICING_PRODUCTS.find((product) => product.key === toPlan);
        assertIsDefined(targetSitePlan);
        if (preview.type === 'checkout') {
            return {
                kind: 'upgrade',
                leadingIcon: Icon.Upgrade,
                label: `Upgrade to ${capitalizeFirstLetter(targetSitePlan.title)}`,
                onPress: async () => {
                    if (preview.invoice) {
                        // Send user to the billing step, e.g. invoice preview
                        openPlanModal(site, {
                            tab: 'billing',
                            type: toPlan,
                            reason,
                        });
                    } else {
                        // Execute update, it will either open the upgrade status modal or send user to Stripe.
                        await updateSitePlan(toPlan);
                    }
                },
            };
        }

        if (preview.type === 'go-ahead') {
            return {
                kind: 'primary',
                label: canStartTrial ? `Try ${targetSitePlan.title}` : 'Switch plan',
                onPress: async () => {
                    await updateSitePlan(toPlan);
                },
            };
        }

        assertNever(preview);
    })();

    return <Button {...buttonProps} loadable />;
}

function renderVisitorAuthCopy({ sitePlan }: { sitePlan: SitePricingDefinition }) {
    return (
        <>
            <p>
                With authenticated access, you control who can access your published docs. So you
                can publish sensitive product documentation and internal content to specific
                audiences — with strict authentication.
            </p>
            <p>
                Upgrade to an {sitePlan.title} site to use your own custom auth server, or set up
                authenticated access with one of our built-in integrations:{' '}
                <DocumentationLink
                    host="developer"
                    path="authenticated-access/guides/integrations/how-to-use-okta-integration-for-authenticated-access"
                >
                    Okta
                </DocumentationLink>
                ,{' '}
                <DocumentationLink
                    host="developer"
                    path="authenticated-access/guides/integrations/how-to-use-auth0-integration-for-authenticated-access"
                >
                    Auth0
                </DocumentationLink>
                , or{' '}
                <DocumentationLink
                    host="developer"
                    path="authenticated-access/guides/integrations/how-to-use-azure-ad-integration-for-authenticated-access"
                >
                    Azure AD
                </DocumentationLink>
                .
            </p>
        </>
    );
}

function getUpgradeDialogCopy({
    featureTrigger,
    targetSitePlan,
    site,
    trialStatus,
}: {
    featureTrigger: SiteFeatureDefinition | undefined;
    targetSitePlan: SitePricingDefinition;
    site: api.Site;
    trialStatus: api.BillingTrialStatus;
}) {
    /* This dialog will require starting a trial if a site is published and the org has an available trial. */
    const willStartTrial = trialStatus === api.BillingTrialStatus.None && site.published;

    const getModalTitle = () => {
        if (!featureTrigger) {
            return willStartTrial
                ? `Trial ${targetSitePlan.title} free for 14 days`
                : `Upgrade to ${targetSitePlan.title}`;
        }
        return willStartTrial
            ? `${featureTrigger.upgradeTitle} with a${targetSitePlan.key === api.SiteType.Ultimate ? 'n' : ''} ${targetSitePlan.title} trial`
            : `${featureTrigger.upgradeTitle} with ${targetSitePlan.title}`;
    };

    const getFeatureTriggerParagraph = () => {
        if (featureTrigger) {
            switch (featureTrigger.key) {
                /* Only for VA we have more elaborate rich text that cant come from table_sites_features */
                case api.SiteFeatureKey.SitesVisitorAuthentication:
                    return renderVisitorAuthCopy({ sitePlan: targetSitePlan });
                default:
                    return <p>{featureTrigger.upgradeDescription}</p>;
            }
        }

        // In rare cases we don't have a feature trigger, e.g. when we click "Try again" in the "Upgrade unsuccessful" dialog.
        switch (targetSitePlan.key) {
            case api.SiteType.Premium:
                return (
                    <p>
                        A {targetSitePlan.title} site unlocks features like advanced customizations
                        and additional audience settings to control who can visit your site.
                    </p>
                );
            case api.SiteType.Ultimate:
                return (
                    <p>
                        An {targetSitePlan.title} site unlocks all features for your site, including
                        authenticated access and site sections.
                    </p>
                );
            default:
                return null;
        }
    };

    const getCheckoutInformation = () => {
        if (site.published) {
            if (trialStatus === api.BillingTrialStatus.None) {
                return `Try ${targetSitePlan.title} on our free trial, no credit card required.`;
            }
        } else {
            if (trialStatus === api.BillingTrialStatus.None) {
                return (
                    <Alert
                        kind="info"
                        size="xsmall"
                        details="Once you publish, we'll start your 14-day free trial."
                    />
                );
            }
            return (
                <Alert
                    kind="info"
                    size="xsmall"
                    details="You won't be charged until your doc site is published."
                />
            );
        }
    };

    return {
        modalTitle: getModalTitle(),
        featureTriggerParagraph: getFeatureTriggerParagraph(),
        checkoutInformation: getCheckoutInformation(),
    };
}
